import { SparklesIcon } from 'lucide-react';

import { useTranslations } from '@northladder/i18n';
import { useBuyerAppConfig, TBidTag } from '@northladder/services';

import { cn } from '../../../../lib';
import {
  Button,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../ui';

import { AnimatedBorder } from '../../../animated-bits';
import { CircularProgressLoader } from '../../../loaders';
import { DisplayCurrency } from '../../../formatting';

import { BidRankBadge } from './BidRankBadge';
import {
  getRankBadgeOptionsFromTag,
} from './config';
import { useBidRankTranslations } from './useBidRankTranslations';

interface IBidRankHoverCardProps {
  isUpdateAllowed?: boolean;
  isMakeMeRankOneLoading?: boolean;
  newRankOneBidAmount?: number;
  onMakeMeRankOne?: () => void;
  rank: TBidTag;
}

/**
 * -----------------------------------------------------------------------------
 * Shows the snippet of the current bid item based on the rank.
 */
export function BidRankHoverCard({
  isUpdateAllowed = false,
  isMakeMeRankOneLoading = false,
  newRankOneBidAmount,
  onMakeMeRankOne,
  rank,
}: IBidRankHoverCardProps) {
  const t = useTranslations('DesignSystemComponents.Bidding');
  const { label, title, description } = useBidRankTranslations(rank);
  const { canPlaceSingleBid } = useBuyerAppConfig().bids;
  const { className, Icon } = getRankBadgeOptionsFromTag(rank);

  const handleMakeMeRankOne = () => {
    onMakeMeRankOne?.();
  };

  /**
   * Determines whether the "Make me Rank 1" button should be shown
   * - Hide this for old bids and bids that are already rank one.
   * - Don't recommend for no-bids since there would be nothing to outbid.
   */
  const shouldShowMakeMeRank1 =
    canPlaceSingleBid &&
    isUpdateAllowed &&
    !(rank === 'rank-one' || rank === 'no-bids');

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <Button className="text-nav-link-text rounded-lg px-0" variant="ghost">
          <BidRankBadge rank={rank} />
        </Button>
      </HoverCardTrigger>

      <HoverCardContent className="w-sm rounded-2xl border py-6">
        <div className="flex justify-between gap-4">
          <div
            className={cn(
              '!size-12 border-1 flex shrink-0 items-center justify-center rounded-full',
              className
            )}
          >
            <Icon className="size-6 block stroke-1" />
          </div>

          <div className="space-y-3">
            <h4 className="text-md font-semibold">{label}</h4>
            <p className="text-sm">{title}</p>
            <p className="text-text-caption text-xs">{description}</p>
            {shouldShowMakeMeRank1 ? (
              <div className="flex items-center gap-2">
                <AnimatedBorder
                  as={Button}
                  className="rounded-md p-0 text-xs text-orange-600 dark:text-orange-500"
                  color="var(--color-orange-500)"
                  disabled={isMakeMeRankOneLoading}
                  onClick={handleMakeMeRankOne}
                  speed="5s"
                  thickness={0.125}
                  variant="outline"
                >
                  {isMakeMeRankOneLoading ? (
                    <CircularProgressLoader className="me-1 size-3.5 inline-block" />
                  ) : (
                    <SparklesIcon className="me-1 inline-block" />
                  )}
                  {t('makeMeRankOne')}
                </AnimatedBorder>

                {newRankOneBidAmount ? (
                  <p className="inline-flex gap-1 p-2 font-mono text-sm items-center">
                    <DisplayCurrency className="font-light" />
                    <span className="text-text-body/80">
                      {newRankOneBidAmount.toLocaleString()}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
